/**
 * Data freshness for the System Health page: for each of the five data
 * systems, how old is what we hold, how often is it refreshed, and is it
 * running to its refresh schedule.
 *
 * Every value is derived from the feed-health read model
 * (GET /ingestion/manage/feeds, zeecore FeedHealthStore). No cadence is written
 * down here: it is the feed's own *usual gap*, learned by the backend from the
 * feed's recent calls, and each feed is judged against its own.
 *
 * What the states mean. They describe the REFRESH SCHEDULE only: whether a
 * fetch happened when it usually does. They say nothing about the quality,
 * completeness or confidence of the data (Constitution §13.2: provenance
 * class, completeness and confidence are separate axes and are not collapsed
 * into this). A feed with no learned cadence gets no schedule verdict at all:
 * it shows its age in a neutral band, which is a display grouping, not a
 * freshness judgement.
 */

import { formatUkDate, formatUkDateTime, formatUkTime, ukClockParts } from '../lib/calc/clock';

export type FreshnessSystemId = 'orders' | 'drivers' | 'telematics' | 'fuel' | 'maintenance';

/**
 * SCHEDULE RULE, per feed, with age = now minus last successful fetch and
 * cadence = the feed's usual gap:
 *   On schedule  age <= cadence x (1 + GRACE_CADENCES)
 *   Running late then up to OVERDUE_AFTER_CADENCES x cadence
 *   Overdue      more than OVERDUE_AFTER_CADENCES x cadence
 * The grace period absorbs jitter around an on-time run (a 15-minute feed
 * that last ran 16 minutes ago is on schedule). Two cadences before Overdue:
 * one missed run is a retry, two in a row is a problem. The backend's own
 * overdue alarm uses 1.5x; this page applies its own explicit constants so the
 * rule shown to users is the rule in this file.
 */
export const GRACE_CADENCES = 0.25;
export const OVERDUE_AFTER_CADENCES = 2;

/**
 * AGE BANDS for feeds with no learned cadence. Display groupings only, not a
 * verdict: Recent is under 24 h, This week is 24 h to 7 days, Old is over 7 days.
 */
export const AGE_BAND_RECENT_MAX_MS = 24 * 3_600_000;
export const AGE_BAND_WEEK_MAX_MS = 7 * 24 * 3_600_000;

/**
 * SOURCES THAT ARE UPLOADED BY HAND, never pulled on a schedule. The one place
 * this is decided. eflex (Keyfuels fuel CSV) is push-only: its only entry point
 * is the upload route (zeecore ingest/sources/eflex/routes.ts), it has no pull
 * endpoint in the scheduled ingest list; each successful upload is recorded as
 * the "Eflex Upload" feed (zeecore ingest/sources/eflex/recordUpload.ts). A long
 * gap since the last upload is therefore not a fault. jigsaw is NOT listed: it
 * is a scheduled pull. A system only reads as manual when none of its feeds has
 * a learned schedule, so a scheduled feed always overrides this list.
 */
export const MANUAL_UPLOAD_SOURCES: ReadonlySet<string> = new Set(['eflex']);

/**
 * HOW OFTEN A HAND-UPLOADED SOURCE SHOULD BE UPLOADED. A business expectation
 * set by the client (fuel is uploaded weekly), NOT learned from the data and
 * not a measured cadence. Age within it reads Up to date; beyond it, Upload
 * due. Applies to every source in MANUAL_UPLOAD_SOURCES.
 */
export const MANUAL_UPLOAD_EXPECTED_INTERVAL_MS = 7 * 24 * 3_600_000;

/**
 * NEWEST DATA. A source can be read on schedule and still bring nothing new:
 * in production in October 2026 the Samsara backfill ran every night while no
 * GPS position landed after 8 Oct, and this page said Telematics was Up to
 * date. Where the page knows the newest day with data (Telematics, from
 * positions per day), a row whose newest data is older than this many days
 * reads "No new data", whatever its schedule says. Two days allows for a
 * nightly backfill (it brings yesterday) and the UTC/UK day edge; vehicles
 * report at weekends too.
 */
export const NEWEST_DATA_MAX_AGE_DAYS: Partial<Record<FreshnessSystemId, number>> = { telematics: 2 };

/** The newest day with data in a series the page read: `day` null = none in the `windowDays` read. */
export interface NewestData {
  day: string | null;
  windowDays: number;
}

/** Friendly names for the sources the panel names; anything else is its raw id, capitalised. */
const SOURCE_NAMES: Record<string, string> = {
  palletways: 'Palletways',
  aquarius: 'Aquarius',
  samsara: 'Samsara',
  optifleet: 'Optifleet',
  supatrak: 'Supatrak',
  jigsaw: 'Jigsaw',
  eflex: 'Keyfuels',
  qargo: 'Qargo',
};

export function friendlySourceName(id: string): string {
  const key = id.trim().toLowerCase();
  return SOURCE_NAMES[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');
}

export const FRESHNESS_SYSTEMS: ReadonlyArray<{ id: FreshnessSystemId; label: string }> = [
  { id: 'orders', label: 'Orders' },
  { id: 'drivers', label: 'Drivers' },
  { id: 'telematics', label: 'Telematics' },
  { id: 'fuel', label: 'Fuel' },
  { id: 'maintenance', label: 'Maintenance' },
];

/** The fields of a /ingestion/manage/feeds entry this page reads. */
export interface FeedHealthEntry {
  feed: string;
  source: string;
  label: string;
  last_attempt_at?: string;
  last_success_at?: string;
  consecutive_failures?: number;
  status?: 'ok' | 'failing' | 'overdue' | 'not_expected';
  /** Learned from recent calls; absent until there are enough of them. */
  usual_gap_ms?: number;
  not_expected_reason?: string;
}

export type AgeBand = 'recent' | 'this_week' | 'old';

/** Schedule state of one feed. `no_schedule` = no learned cadence (see AgeBand); `unknown` = no successful fetch recorded. */
export type FeedScheduleState = 'on_schedule' | 'running_late' | 'overdue' | 'no_schedule' | 'unknown';
export type FreshnessState = FeedScheduleState | 'upload_due' | 'not_configured' | 'no_new_data';

export interface FeedFreshness {
  label: string;
  source: string;
  state: FeedScheduleState;
  /** Only for `no_schedule`. */
  ageBand: AgeBand | null;
  lastSuccessAt: string | null;
  cadenceMs: number | null;
  ageMs: number | null;
  failing: boolean;
}

export interface SourceLabel {
  name: string;
  /** Uploaded by hand (shows the Manual upload badge next to its name). */
  manual: boolean;
}

export interface FreshnessRow {
  id: FreshnessSystemId;
  label: string;
  /** Where the data comes from, in plain words. */
  sources: SourceLabel[];
  /** The system is fed by hand-uploaded data: judged against the expected upload interval. */
  manualUpload: boolean;
  /** The worst state among the system's expected feeds. */
  state: FreshnessState;
  ageBand: AgeBand | null;
  /** Oldest last successful fetch among the expected feeds that have one. */
  lastRefreshedAt: string | null;
  /** Cadence of the feed that decides the state. */
  cadenceMs: number | null;
  /** Label of the feed that decides the state. */
  decidingFeed: string | null;
  /** Expected feeds whose last call failed. */
  failingFeeds: number;
  expectedFeeds: number;
  /** One-line reason a row is not on schedule. */
  reason: string | null;
}

/**
 * Which data system a feed belongs to. A grouping of the backend's feed
 * labels, not data: Samsara feeds three systems, so its label decides.
 */
export function classifyFeed(feed: Pick<FeedHealthEntry, 'source' | 'label'>): FreshnessSystemId | null {
  const source = feed.source.toLowerCase();
  const label = feed.label.toLowerCase();
  if (/maintenance|fault code/.test(label)) return 'maintenance';
  // Samsara's daily fuel & energy reports: not telematics (a failed nightly
  // report would mark GPS as failing), and not the Fuel row, which is judged
  // as hand uploads. Feed health still lists it on its own.
  if (/fuel & energy/.test(label)) return null;
  if (/tachograph|driver|shift|violation|compliance|activity/.test(label)
    && (source === 'aquarius' || source === 'samsara')) return 'drivers';
  if (['optifleet', 'supatrak', 'samsara'].includes(source)) return 'telematics';
  if (['palletways', 'qargo'].includes(source)) return 'orders';
  if (['jigsaw', 'eflex'].includes(source)) return 'fuel';
  return null;
}

/** Whether a source id is one this page can show (used to tell "not connected" from "no records"). */
export function sourceSystems(source: string): FreshnessSystemId[] {
  switch (source.toLowerCase()) {
    case 'palletways':
    case 'qargo': return ['orders'];
    case 'aquarius': return ['drivers', 'maintenance'];
    case 'samsara': return ['telematics', 'drivers', 'maintenance'];
    case 'optifleet':
    case 'supatrak': return ['telematics'];
    case 'jigsaw':
    case 'eflex': return ['fuel'];
    default: return [];
  }
}

export function ageBandOf(ageMs: number): AgeBand {
  if (ageMs < AGE_BAND_RECENT_MAX_MS) return 'recent';
  if (ageMs <= AGE_BAND_WEEK_MAX_MS) return 'this_week';
  return 'old';
}

/** One feed's schedule state at `now` (epoch ms), judged against its own cadence. */
export function evaluateFeedFreshness(feed: FeedHealthEntry, now: number): FeedFreshness {
  const successMs = feed.last_success_at ? Date.parse(feed.last_success_at) : NaN;
  const lastSuccessAt = Number.isFinite(successMs) ? feed.last_success_at! : null;
  const cadenceMs = typeof feed.usual_gap_ms === 'number' && feed.usual_gap_ms > 0 ? feed.usual_gap_ms : null;
  const ageMs = lastSuccessAt ? Math.max(0, now - successMs) : null;
  const failing = feed.status === 'failing';
  let state: FeedScheduleState = 'unknown';
  let ageBand: AgeBand | null = null;
  if (ageMs != null && cadenceMs != null) {
    state = ageMs > OVERDUE_AFTER_CADENCES * cadenceMs ? 'overdue'
      : ageMs > (1 + GRACE_CADENCES) * cadenceMs ? 'running_late'
        : 'on_schedule';
  } else if (ageMs != null) {
    state = 'no_schedule';
    ageBand = ageBandOf(ageMs);
  }
  return { label: feed.label, source: feed.source, state, ageBand, lastSuccessAt, cadenceMs, ageMs, failing };
}

/** How badly a feed needs attention; the highest-ranked feed decides its system's row. */
function severity(f: Pick<FeedFreshness, 'state' | 'ageBand'>): number {
  switch (f.state) {
    case 'overdue': return 5;
    case 'no_schedule': return f.ageBand === 'old' ? 4 : 1;
    case 'running_late': return 3;
    case 'unknown': return 2;
    default: return 0;
  }
}

/** Whether a row has a known update schedule (its deciding feed has a learned cadence). */
export function hasKnownSchedule(row: Pick<FreshnessRow, 'state' | 'manualUpload'>): boolean {
  return (!row.manualUpload && row.state === 'on_schedule')
    || row.state === 'running_late' || row.state === 'overdue' || row.state === 'no_new_data';
}

const dayKeyNumber = (ymd: string): number | null => {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000 : null;
};

/**
 * A row read on schedule (or late, or with no schedule) whose newest data is
 * too old, or absent from the days read, is "No new data". Overdue, Unknown and
 * the rest already say more, so they are left as they are; so is a row whose
 * series could not be read (no `newest`).
 */
function withNewestData(row: FreshnessRow, newest: NewestData | undefined, now: number): FreshnessRow {
  const maxAge = NEWEST_DATA_MAX_AGE_DAYS[row.id];
  if (maxAge == null || !newest) return row;
  if (row.state !== 'on_schedule' && row.state !== 'running_late' && row.state !== 'no_schedule') return row;
  const today = dayNumber(new Date(now));
  const newestDay = newest.day ? dayKeyNumber(newest.day) : null;
  if (today != null && newestDay != null && today - newestDay <= maxAge) return row;
  return {
    ...row,
    state: 'no_new_data',
    ageBand: null,
    reason: newest.day
      ? `No new data since ${formatDayKey(newest.day)}, though the source is still being read`
      : `No new data in the last ${newest.windowDays} days, though the source is still being read`,
  };
}

/**
 * The five rows: systems with a known update schedule first, the rest last
 * (fixed system order within each group). `enabledSources` (org ingest sources) tells a system with no feed
 * records that is genuinely not connected from one that should have data.
 * `newestData`: the newest day with data, for the systems whose series was read
 * (see NEWEST_DATA_MAX_AGE_DAYS).
 */
export function buildFreshnessRows(
  feeds: readonly FeedHealthEntry[],
  enabledSources: readonly string[],
  now: number,
  newestData: Partial<Record<FreshnessSystemId, NewestData>> = {},
): FreshnessRow[] {
  const rows = FRESHNESS_SYSTEMS.map(({ id, label }) => {
    const mine = feeds.filter((f) => classifyFeed(f) === id);
    const expected = mine.filter((f) => f.status !== 'not_expected');
    const connected = enabledSources.some((s) => sourceSystems(s).includes(id));

    const sourceIds = [...new Set([
      ...expected.map((f) => f.source.toLowerCase()),
      ...enabledSources.map((x) => x.toLowerCase()).filter((x) => sourceSystems(x).includes(id)),
    ])];
    const sources: SourceLabel[] = sourceIds.map((x) => ({
      name: friendlySourceName(x),
      manual: MANUAL_UPLOAD_SOURCES.has(x),
    }));

    // Manual upload: a hand-uploaded source is connected and nothing for this
    // system has a learned schedule. Judged against the expected upload
    // interval. The last upload comes only from a recorded successful import
    // (the "Eflex Upload" feed); until one is recorded it is Unknown.
    const manualFeeds = mine.filter((f) => MANUAL_UPLOAD_SOURCES.has(f.source.toLowerCase()));
    const manualConnected = manualFeeds.length > 0
      || enabledSources.some((src) => MANUAL_UPLOAD_SOURCES.has(src.toLowerCase()) && sourceSystems(src).includes(id));
    // Only a pulled feed's schedule counts: a gap learned between hand uploads is not a schedule.
    const hasLearnedSchedule = expected
      .filter((f) => !MANUAL_UPLOAD_SOURCES.has(f.source.toLowerCase()))
      .some((f) => evaluateFeedFreshness(f, now).cadenceMs != null);
    if (manualConnected && !hasLearnedSchedule) {
      const uploads = manualFeeds
        .map((f) => f.last_success_at)
        .filter((t): t is string => !!t && Number.isFinite(Date.parse(t)))
        .sort();
      const last = uploads.length > 0 ? uploads[uploads.length - 1] : null;
      const age = last ? Math.max(0, now - Date.parse(last)) : null;
      const state: FreshnessState = age == null ? 'unknown'
        : age > MANUAL_UPLOAD_EXPECTED_INTERVAL_MS ? 'upload_due' : 'on_schedule';
      return {
        ...blankRow(id, label, state, ''),
        sources,
        manualUpload: true,
        lastRefreshedAt: last,
        reason: state === 'upload_due'
          ? `Upload due: expected ${formatUploadInterval(MANUAL_UPLOAD_EXPECTED_INTERVAL_MS).toLowerCase()}, last uploaded ${formatAgeWords(last, now)}`
          : state === 'unknown' ? 'No upload date is recorded' : null,
      } satisfies FreshnessRow;
    }

    if (expected.length === 0) {
      if (mine.length === 0 && !connected) {
        return { ...blankRow(id, label, 'not_configured', 'No source connected for this system'), sources };
      }
      return {
        ...blankRow(id, label, 'unknown', mine.length === 0
          ? 'No updates recorded yet'
          : 'No automatic updates are set up'),
        sources,
      };
    }

    const evaluated = expected.map((f) => evaluateFeedFreshness(f, now));
    const failingFeeds = evaluated.filter((e) => e.failing).length;
    const succeeded = evaluated.filter((e) => e.lastSuccessAt != null);
    const oldest = succeeded.reduce<FeedFreshness | null>(
      (acc, e) => (acc == null || Date.parse(e.lastSuccessAt!) < Date.parse(acc.lastSuccessAt!) ? e : acc),
      null,
    );

    // Worst feed decides; ties go to the older success (the claim must hold for all feeds).
    const decider = evaluated.reduce((a, b) => {
      const d = severity(b) - severity(a);
      if (d !== 0) return d > 0 ? b : a;
      return (b.ageMs ?? -1) > (a.ageMs ?? -1) ? b : a;
    });

    // User-visible wording: plain language, no internal terms.
    const who = expected.length > 1 ? `${decider.label}: ` : '';
    const lastUpdated = formatAgeWords(decider.lastSuccessAt, now);
    let reason: string | null = null;
    switch (decider.state) {
      case 'overdue':
        reason = `${who}Overdue: expected ${formatCadence(decider.cadenceMs).toLowerCase()}, last updated ${lastUpdated}`;
        break;
      case 'running_late':
        reason = `${who}Running late: expected ${formatCadence(decider.cadenceMs).toLowerCase()}, last updated ${lastUpdated}`;
        break;
      case 'no_schedule':
        reason = `${who}Last updated ${lastUpdated} and no regular schedule`;
        break;
      case 'unknown':
        reason = `${decider.label} has not updated successfully`;
        break;
      default:
        break;
    }

    return {
      id,
      label,
      sources,
      manualUpload: false,
      state: decider.state,
      ageBand: decider.ageBand,
      lastRefreshedAt: oldest?.lastSuccessAt ?? null,
      cadenceMs: decider.cadenceMs,
      decidingFeed: decider.label,
      failingFeeds,
      expectedFeeds: expected.length,
      reason,
    } satisfies FreshnessRow;
  });
  return rows
    .map((row) => withNewestData(row, newestData[row.id], now))
    .map((row, i) => ({ row, i }))
    .sort((a, b) => Number(!hasKnownSchedule(a.row)) - Number(!hasKnownSchedule(b.row)) || a.i - b.i)
    .map((x) => x.row);
}

function blankRow(
  id: FreshnessSystemId,
  label: string,
  state: FreshnessState,
  reason: string,
): FreshnessRow {
  return {
    id, label, sources: [], manualUpload: false, state, ageBand: null, lastRefreshedAt: null, cadenceMs: null, decidingFeed: null,
    failingFeeds: 0, expectedFeeds: 0, reason,
  };
}

/** "Weekly upload", "Daily upload", else "Upload every N days": the expected interval, from the constant. */
export function formatUploadInterval(ms: number): string {
  const days = Math.round(ms / 86_400_000);
  if (days === 1) return 'Daily upload';
  if (days === 7) return 'Weekly upload';
  return `Upload every ${days} days`;
}

/** "Every 15 min", "Every 3 h", "Every 1 d" (the learned gap, rounded); "No regular updates" when there is none. */
export function formatCadence(ms: number | null): string {
  if (ms == null || !(ms > 0)) return 'No regular updates';
  const min = ms / 60_000;
  if (min < 90) return `Every ${Math.max(1, Math.round(min))} min`;
  const h = min / 60;
  if (h < 36) return `Every ${Number(h.toFixed(1))} h`;
  return `Every ${Math.round(h / 24)} d`;
}

const dayNumber = (iso: string | Date): number | null => {
  const p = ukClockParts(iso);
  return p ? Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)) / 86_400_000 : null;
};

/**
 * Age in plain words, on the UK clock: "just now", "12 min ago", "today 09:40",
 * "yesterday 22:15", "4 days ago", "3 weeks ago", "2 months ago".
 * Unknown only when there is no usable last-success time.
 */
export function formatAgeWords(iso: string | null, now: number): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return 'Unknown';
  const ageMs = Math.max(0, now - t);
  const min = Math.floor(ageMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const days = (dayNumber(new Date(now)) ?? 0) - (dayNumber(iso!) ?? 0);
  if (days <= 0) return `today ${formatUkTime(iso!)}`;
  if (days === 1) return `yesterday ${formatUkTime(iso!)}`;
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/** UK wall-clock time of an instant, or Unknown. TODO: switch to utils/ukTime once PR #977 (uk-time-display) is merged. */
export function formatInstant(iso: string | null): string {
  return iso ? formatUkDateTime(iso, 'Unknown') : 'Unknown';
}

/** A day key (YYYY-MM-DD) as "19 Oct", or Unknown. Day-grain only: no time of day is claimed. */
export function formatDayKey(day: string | null): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return 'Unknown';
  return formatUkDate(`${day}T12:00:00`, 'Unknown');
}

/** Newest day with data in a daily series, by date key; null when no day has any. */
export function newestDayWithData<T extends { date?: string }>(
  series: readonly T[],
  hasData: (d: T) => boolean,
): string | null {
  const days = series.filter((d) => d.date && hasData(d)).map((d) => d.date as string).sort();
  return days.length > 0 ? days[days.length - 1] : null;
}
